import { Paths, pathToEditCatalog } from "@app/routes";
import { ConfirmAction } from "@atlas/composite";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  ScrollArea,
  useToast,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IHubCatalog, IHubPackage } from "@models";
import { useDeleteHubCatalog } from "@query";
import { cva } from "class-variance-authority";
import { ChevronDown, ChevronUp, Pencil, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { createSearchParams, useNavigate } from "react-router";
import {
  CatalogItemCard,
  type IHubCatalogOwnerActionItem,
} from "./CatalogItemCard";
import type { HubManagerTabId } from "./hubManagerCatalogFilters";
import { HUB_MANAGER_CATALOGS_PREVIEW_LIMIT } from "./hubManagerCatalogFilters";

const catalogTypeSectionGridVariants = cva(
  "grid auto-rows-auto grid-cols-[repeat(auto-fill,345px)] gap-4 justify-start",
);

interface ICatalogTypeSectionProps {
  typeDescription: string;
  typeLabel: string;
  hubManagerTab: HubManagerTabId;
  cartPackage?: IHubPackage;
  catalogs: IHubCatalog[];
  totalCount: number;
  isLoading: boolean;
  isRefetching?: boolean;
  onViewAll: () => void;
  previewLimit?: number;
  selectedForExport?: Set<string>;
  onExportToggle?: (catalogId: number, version: number) => void;
}

function catalogDetailsNavigateTo(
  catalogId: number,
  version: string,
  hubManagerTab: HubManagerTabId,
  isExplorerBrowse: boolean,
) {
  return {
    pathname: `/${Paths.CatalogManager}/${Paths.CatalogDetails}`,
    search: createSearchParams({
      tab: isExplorerBrowse ? "explorer" : hubManagerTab,
      catalogId: String(catalogId),
      version: version,
    }).toString(),
  };
}

export function CatalogTypeSection({
  typeDescription,
  typeLabel,
  hubManagerTab,
  cartPackage,
  catalogs,
  totalCount,
  isLoading,
  isRefetching = false,
  onViewAll,
  previewLimit = HUB_MANAGER_CATALOGS_PREVIEW_LIMIT,
  selectedForExport,
  onExportToggle,
}: ICatalogTypeSectionProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const { mutate: deleteCatalog } = useDeleteHubCatalog();
  const [isViewAllExpanded, setIsViewAllExpanded] = useState(false);
  const isExplorerBrowse = hubManagerTab === "explorer";
  const [deleteTarget, setDeleteTarget] = useState<IHubCatalog | null>(null);

  const handleMoreOptions = useCallback(
    (action: string, catalog?: IHubCatalog) => {
      if (action === "details" && catalog?.catalogId != null) {
        navigate(
          catalogDetailsNavigateTo(
            catalog.catalogId,
            String(catalog.version),
            hubManagerTab,
            isExplorerBrowse,
          ),
        );
        return;
      }
      if (action === "edit" && catalog?.catalogId) {
        navigate(pathToEditCatalog(catalog.catalogId, String(catalog.version)));
      }
      if (action === "delete" && catalog) {
        setDeleteTarget(catalog);
      }
    },
    [navigate, hubManagerTab, isExplorerBrowse],
  );

  const getOwnerMenuItems = useCallback(
    (catalog: IHubCatalog): IHubCatalogOwnerActionItem[] => [
      {
        id: "edit",
        icon: Pencil,
        variant: "ghost",
        ariaLabel: t("hub.administer.edit", { defaultValue: "Edit" }),
        onSelect: () => handleMoreOptions("edit", catalog),
      },
      {
        id: "delete",
        icon: Trash2,
        variant: "destructive",
        ariaLabel: t("hub.catalog_manager.delete", { defaultValue: "Delete" }),
        onSelect: () => handleMoreOptions("delete", catalog),
      },
    ],
    [handleMoreOptions, t],
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget?.catalogId || !deleteTarget?.version) return;
    deleteCatalog(
      {
        catalogId: deleteTarget.catalogId,
        versionId: String(deleteTarget.version),
      },
      {
        onSuccess: () => {
          setDeleteTarget(null);
          toast.add({
            title: t("hub.catalog_manager.delete_success", {
              defaultValue: "Catalog deleted",
            }),
            data: { type: "success" },
          });
        },
        onError: (error: unknown) => {
          const apiError = error as { status?: number; message?: string };
          toast.add({
            title: t("hub.catalog_manager.delete_error", {
              defaultValue: "Failed to delete catalog",
            }),
            ...(apiError.status === 409 && apiError.message
              ? { description: apiError.message }
              : {}),
            data: { type: "error" },
          });
        },
      },
    );
  }, [deleteTarget, deleteCatalog, toast, t]);

  const handleViewAll = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsViewAllExpanded(true);
      onViewAll();
    },
    [onViewAll],
  );

  const handleHideAll = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsViewAllExpanded(false);
  }, []);

  const displayedCatalogs = isViewAllExpanded
    ? catalogs
    : catalogs.slice(0, previewLimit);

  const viewAllControls =
    totalCount > previewLimit ? (
      <div className="flex shrink-0 items-center gap-4">
        {!isViewAllExpanded && (
          <Button variant="link" size="xs" onClick={handleViewAll}>
            {t("hub.catalog_manager.view_all", {
              defaultValue: "View All",
            })}{" "}
            ({totalCount})
            <ChevronDown className="ml-1 size-4" />
          </Button>
        )}
        {isViewAllExpanded && (
          <Button variant="link" size="xs" onClick={handleHideAll}>
            {t("hub.catalog_manager.collapse", {
              defaultValue: "Collapse",
            })}
            <ChevronUp className="ml-1 size-4" />
          </Button>
        )}
      </div>
    ) : null;

  const catalogCards = displayedCatalogs.map(
    (catalog: IHubCatalog, index: number) => (
      <CatalogItemCard
        key={`${catalog.catalogId ?? "catalog"}-${catalog.version ?? 0}-${index}`}
        catalog={catalog}
        onMoreOptions={handleMoreOptions}
        ownerActionsMenuItems={getOwnerMenuItems(catalog)}
        showLifecycleBadge={hubManagerTab !== "explorer"}
        showOwnerActionsMenu={hubManagerTab !== "explorer"}
        showSubscribeAction={isExplorerBrowse}
        cartPackage={cartPackage}
        isExportSelected={selectedForExport?.has(
          `${catalog.catalogId}-${catalog.version}`,
        )}
        onExportToggle={onExportToggle}
      />
    ),
  );

  const deleteConfirm = (
    <ConfirmAction
      open={!!deleteTarget}
      onOpenChange={(open) => !open && setDeleteTarget(null)}
      title={t("hub.catalog_manager.delete_title", {
        defaultValue: "Delete Catalog",
      })}
      description={t("hub.catalog_type_manager.delete_description", {
        name: deleteTarget?.label ?? deleteTarget?.name ?? "this catalog",
        defaultValue:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
      })}
      confirmText={t("hub.catalog_manager.delete", {
        defaultValue: "Delete",
      })}
      cancelText={t("hub.administer.cancel", { defaultValue: "Cancel" })}
      actionVariant="destructive"
      onConfirm={handleDeleteConfirm}
    />
  );

  const sectionBusy =
    isLoading || (isExplorerBrowse && isRefetching && catalogs.length === 0);

  if (!isExplorerBrowse && !isLoading && (catalogs?.length ?? 0) === 0) {
    return null;
  }

  if (
    isExplorerBrowse &&
    !sectionBusy &&
    (catalogs?.length ?? 0) === 0 &&
    totalCount === 0
  ) {
    return null;
  }

  const showCatalogSkeleton = sectionBusy && displayedCatalogs.length === 0;
  const skeletonCount = Math.max(1, previewLimit);
  const skeletonKeys = Array.from(
    { length: skeletonCount },
    (_, index) => `catalog-skeleton-${typeLabel}-${index}`,
  );

  return (
    <section className="flex flex-col gap-4">
      <Card
        variant="content"
        className="border-0 shadow-none"
        isLoading={sectionBusy && !showCatalogSkeleton}>
        <CardHeader className="flex flex-row items-start justify-between gap-2 sticky top-0 z-1 bg-background py-2 mr-5 text-lg font-bold">
          <div className="flex flex-col text-left gap-2">
            <CardTitle>{typeLabel}</CardTitle>
            {typeDescription ? (
              <CardDescription>{typeDescription}</CardDescription>
            ) : null}
          </div>
          {viewAllControls}
        </CardHeader>
        <CardContent>
          <ScrollArea
            className="w-full min-w-0 pb-1"
            viewportClassName="overscroll-x-contain [-webkit-overflow-scrolling:touch]">
            <div className={cn(catalogTypeSectionGridVariants())}>
              {showCatalogSkeleton
                ? skeletonKeys.map((skeletonKey) => (
                    <Card
                      key={skeletonKey}
                      isLoading
                      className="h-[263px] w-[345px]">
                      <CardContent />
                    </Card>
                  ))
                : catalogCards}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
      {deleteConfirm}
    </section>
  );
}
